import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import useCompanyAuth from "hooks/useCompanyAuth";
import Input from "components/ui/Input";
import Select from "components/ui/Select";
import logo from "assets/img/logo.png";

const CompanyLogin = () => {
  const navigate = useNavigate();
  const { login, selectCompany, isLoggingIn, isSelecting } = useCompanyAuth();

  const [credentials, setCredentials] = useState({
    email: "",
    password: "",
    safecontact: "",
  });

  const [showCompanySelection, setShowCompanySelection] = useState(false);
  const [companyOptions, setCompanyOptions] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [tempCredentials, setTempCredentials] = useState(null);

  const updateCredentials = (e) => {
    const { name, value } = e.target;
    setCredentials((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleLogin = async () => {
    if (
      !credentials.email ||
      !credentials.password ||
      !credentials.safecontact
    ) {
      return;
    }

    try {
      const result = await login(credentials);

      // Check if multiple companies found
      if (result.requiresSelection) {
        // Show company selection dropdown
        const options = result.companies.map((c) => ({
          value: c.id_company,
          label: `${c.id_company} - ${c.company_name}`,
        }));
        setCompanyOptions(options);
        setTempCredentials(result.tempData);
        setShowCompanySelection(true);
      } else {
        // Single company - auth data already saved to store by the hook
        // Navigate to company dashboard
        navigate("/company/dashboard");
      }
    } catch (error) {
      // Error is already handled by the service with toast
      console.error("Login failed:", error);
    }
  };

  const handleCompanySelection = async () => {
    if (!selectedCompany) {
      return;
    }

    try {
      await selectCompany({
        ...tempCredentials,
        selectedCompanyId: selectedCompany.value,
      });

      // Auth data already saved to store by the hook
      // Navigate to company dashboard
      navigate("/company/dashboard");
    } catch (error) {
      console.error("Company selection failed:", error);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      if (showCompanySelection) {
        handleCompanySelection();
      } else {
        handleLogin();
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-900 to-slate-900 flex items-center justify-center px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Logo and Header */}
        <div className="text-center">
          <img
            className="h-16 w-auto mx-auto mb-6"
            src={logo}
            alt="Company Logo"
          />
          <div className="inline-flex items-center justify-center px-4 py-2 rounded-full bg-indigo-900/50 border border-indigo-500/50 mb-4">
            <svg
              className="h-5 w-5 text-indigo-400 mr-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
              />
            </svg>
            <span className="text-indigo-200 text-sm font-medium">
              Company Portal
            </span>
          </div>
        </div>

        {/* Login Card */}
        <div className="bg-white rounded-xl shadow-2xl p-8">
          <div className="mb-6">
            <h3 className="text-xl font-semibold text-gray-900 text-center">
              {showCompanySelection ? "Select Company" : "Company Login"}
            </h3>
            <p className="text-gray-600 text-sm text-center mt-1">
              {showCompanySelection
                ? "Multiple companies found. Please select one to continue."
                : "Manage users, preferences, and company settings"}
            </p>
          </div>

          {!showCompanySelection ? (
            /* Initial Login Form */
            <form className="space-y-5" onKeyPress={handleKeyPress}>
              <Input
                id="email"
                name="email"
                type="email"
                label="Email Address"
                value={credentials.email}
                onChange={updateCredentials}
                placeholder="Enter company email"
                autoComplete="email"
                isRequired
              />

              <Input
                id="safecontact"
                name="safecontact"
                type="text"
                label="Safe Contact (Mobile)"
                value={credentials.safecontact}
                onChange={updateCredentials}
                placeholder="Enter safe contact number"
                autoComplete="tel"
                isRequired
              />

              <Input
                id="password"
                name="password"
                type="password"
                label="Password"
                value={credentials.password}
                onChange={updateCredentials}
                placeholder="Enter password"
                autoComplete="current-password"
                isRequired
              />

              {/* Login Button */}
              <button
                type="button"
                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg"
                onClick={handleLogin}
                disabled={isLoggingIn}
              >
                {isLoggingIn ? (
                  <div className="flex items-center">
                    <svg
                      className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Authenticating...
                  </div>
                ) : (
                  <div className="flex items-center">
                    <svg
                      className="h-5 w-5 mr-2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1"
                      />
                    </svg>
                    Sign In to Company Portal
                  </div>
                )}
              </button>
            </form>
          ) : (
            /* Company Selection Form */
            <form className="space-y-5" onKeyPress={handleKeyPress}>
              <Select
                label="Select Company *"
                options={companyOptions}
                value={selectedCompany}
                onChange={setSelectedCompany}
                placeholder="Choose your company"
                isClearable={false}
                isSearchable={true}
              />

              <div className="flex gap-3">
                <button
                  type="button"
                  className="flex-1 py-3 px-4 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all duration-200"
                  onClick={() => {
                    setShowCompanySelection(false);
                    setSelectedCompany(null);
                    setCompanyOptions([]);
                    setTempCredentials(null);
                  }}
                  disabled={isSelecting}
                >
                  Back
                </button>
                <button
                  type="button"
                  className="flex-1 py-3 px-4 border border-transparent rounded-lg text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg"
                  onClick={handleCompanySelection}
                  disabled={isSelecting || !selectedCompany}
                >
                  {isSelecting ? "Loading..." : "Continue"}
                </button>
              </div>
            </form>
          )}

          {/* Info Message */}
          <div className="mt-6 bg-indigo-50 border border-indigo-200 rounded-lg p-4">
            <div className="flex">
              <svg
                className="h-5 w-5 text-indigo-600 mr-2 flex-shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div>
                <p className="text-sm text-indigo-800 font-medium">
                  Company Access Only
                </p>
                <p className="text-xs text-indigo-600 mt-1">
                  This portal is for company administrators to manage users and
                  settings. Use the regular login for daily operations.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center">
          <p className="text-indigo-200 text-xs">
            © 2024{" "}
            {process.env.REACT_APP_BRAND_NAME || "Business Management System"}.
            All rights reserved.
          </p>
          <button
            onClick={() => navigate("/")}
            className="text-indigo-300 hover:text-indigo-100 text-xs mt-2 transition-colors"
          >
            ← Back to Regular Login
          </button>
        </div>
      </div>
    </div>
  );
};

export default CompanyLogin;
